'use client';

import {
  Button,
  EmptyState,
  ErrorState,
  Grid,
  GridItem,
  LabelValue,
  type LabelValueProps,
  Skeleton,
  Stack,
  Text,
} from '@pie/amber';
import { formatLongIsoDate } from '@upms/dates';
import { useState } from 'react';
import { usePolicyPaymentMethod } from '@/lib/api/billing';
import { PaymentDialog } from '@/components/PaymentDialog';
import { usePolicyPaymentPlan } from '@/hooks/usePolicyPaymentPlan';
import { hasPaymentDue, isPaidInFull } from '@/lib/api/billing/predicates';

const usdFormatter = new Intl.NumberFormat('en-US', {
  currency: 'USD',
  minimumFractionDigits: 2,
  style: 'currency',
});

export const PolicyPaymentInfoLabelValueSkeleton = () => {
  return (
    <Stack direction="row" gap="md">
      <Stack gap="2xs" width="full">
        <Skeleton className="w-1/2" size="body-sm" />
        <Skeleton className="w-1/3" size="body-lg" />
      </Stack>
      <Stack gap="2xs" width="full">
        <Skeleton className="w-1/2" size="body-sm" />
        <Skeleton className="w-1/3" size="body-lg" />
      </Stack>
    </Stack>
  );
};

const PolicyPaymentInfoLabelValue = ({ children, ...props }: LabelValueProps) => {
  return (
    <LabelValue {...props} size="md">
      <Text className="font-body-strong!" variant={{ base: 'body-md', md: 'body-lg' }}>
        {children}
      </Text>
    </LabelValue>
  );
};

type PolicyPaymentInfoProps = {
  policyId: string;
};

export const PolicyPaymentInfo = ({ policyId }: PolicyPaymentInfoProps) => {
  const methodQuery = usePolicyPaymentMethod(policyId);
  const planQuery = usePolicyPaymentPlan(policyId);
  const [isPaymentDialogOpen, setIsPaymentDialogOpen] = useState(false);

  if (methodQuery.status === 'pending' || planQuery.status === 'pending') {
    return <PolicyPaymentInfoLabelValueSkeleton />;
  }

  if (planQuery.status === 'error') {
    return (
      <ErrorState
        heading="Failed to load"
        helpText="We were unable to load the payment info for this policy. Please try again later."
        size="sm"
      />
    );
  }

  switch (planQuery.data.paymentPlan) {
    case 'Annual':
    case 'BiAnnual':
    case 'Quarterly':
    case 'Monthly':
      return (
        <Grid
          gap={{ base: 'layout-xs', sm: 'layout-md' }}
          templateColumns={{ base: '1fr', sm: '1fr 1fr' }}
        >
          <GridItem>
            {isPaidInFull(planQuery.data) || !hasPaymentDue(planQuery.data) ? (
              <PolicyPaymentInfoLabelValue
                helpText="All estimated premium payments are complete."
                label="Premium payments"
              >
                Paid in full
              </PolicyPaymentInfoLabelValue>
            ) : (
              <PolicyPaymentInfoLabelValue
                helpText={
                  planQuery.data.nextPaymentDueDate
                    ? `scheduled for ${formatLongIsoDate(planQuery.data.nextPaymentDueDate)}`
                    : undefined
                }
                label="Next payment"
              >
                {usdFormatter.format(planQuery.data.nextPaymentAmount)}
              </PolicyPaymentInfoLabelValue>
            )}
          </GridItem>
          <GridItem>
            <PolicyPaymentInfoLabelValue
              helpText={
                planQuery.data.paymentPlan === 'Monthly'
                  ? 'billed monthly'
                  : planQuery.data.paymentPlan === 'Quarterly'
                    ? 'billed quarterly'
                    : undefined
              }
              label="Total premium"
            >
              {usdFormatter.format(planQuery.data.totalPremium)}
            </PolicyPaymentInfoLabelValue>
          </GridItem>
        </Grid>
      );
    case 'Payroll':
      return (
        <Grid
          gap={{ base: 'layout-xs', sm: 'layout-md' }}
          templateColumns={{ base: '1fr', sm: '1fr 1fr' }}
        >
          <GridItem>
            <LabelValue
              helpText="Premium payments for your payroll policy are managed through your payroll partner."
              label="Payment plan"
            >
              Payroll Billing
            </LabelValue>
          </GridItem>
          {methodQuery.status === 'error' ? (
            <GridItem>
              <ErrorState
                heading="Failed to load"
                helpText="We were unable to load the payment method for this policy. Please try again later."
                size="sm"
              />
            </GridItem>
          ) : methodQuery.data === undefined ? (
            <GridItem>
              <LabelValue label="Payment method">
                <EmptyState
                  actions={
                    <Button
                      emphasis="heavy"
                      onClick={() => {
                        setIsPaymentDialogOpen(true);
                      }}
                      size="sm"
                      tone="accent"
                    >
                      Add Payment Method
                    </Button>
                  }
                  borderless={false}
                  helpText="Add a payment method to avoid delays if invoicing is needed outside regular payments"
                  size="sm"
                />
              </LabelValue>
              {isPaymentDialogOpen && (
                <PaymentDialog
                  hasExistingPaymentMethod={false}
                  onClose={() => {
                    setIsPaymentDialogOpen(false);
                  }}
                  policyId={policyId}
                />
              )}
            </GridItem>
          ) : null}
        </Grid>
      );
    default:
      // TODO: report unknown payment plan types to Datadog
      return null;
  }
};
